import { Link } from "react-router-dom";
import { ArrowUpRight, Beaker, Leaf, Sun, ShieldCheck, Clock } from "lucide-react";
import TrustBar from "@/components/TrustBar";
import ProductCard from "@/components/ProductCard";
import { PRODUCTS } from "@/data/catalogue";
import { Reveal } from "@/hooks/useInView";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/bf5d062f-6650-4ea8-b4db-e01f22bb1f4b/images/0a19b5749728bc42f60e6f461f32074fcf09c8711f3826aae9a219878c139251.png";
const POWDER_IMG = "https://static.prod-images.emergentagent.com/jobs/bf5d062f-6650-4ea8-b4db-e01f22bb1f4b/images/8c0c54212536ef21ae3beb63daba6c725f2a653b433e04b855cff1e202fc5bd8.png";

const PILLARS = [
  { icon: Sun, title: "India's Climate Advantage", body: "India's cultivation conditions support strong spirulina biomass development and a reliable foundation for high-quality phycocyanin production planning." },
  { icon: ShieldCheck, title: "Purity You Can Measure", body: "Every grade is defined by purity ratio and application fit, from food-color performance to high-spec technical evaluation." },
  { icon: Leaf, title: "Documentation by Design", body: "From cultivation and extraction through release protocols, SPIRUVA is being structured around traceability, testing, and documentation expected by serious buyers." },
  { icon: Beaker, title: "Launch-Ready Market Positioning", body: "The platform is designed for importers, formulators, and distributors who want to prepare their 2027 sourcing strategy before demand tightens." },
];

const STATS = [
  { v: "300+", l: "Cultivation Days / Year" },
  { v: "E18 → E40", l: "Grade Architecture" },
  { v: "Doc-First", l: "Export Planning" },
  { v: "Jul 2027", l: "Commercial Readiness" },
];

const Home = () => {
  const featured = PRODUCTS.filter((p) => ["e18", "e25", "e30", "e40"].includes(p.id));

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[100svh] w-full flex items-center justify-center text-brand-cream overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Spirulina cultivation at dawn" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-black/85 via-brand-black/60 to-brand-black/90" />
          <div className="absolute inset-0 grain opacity-30 mix-blend-overlay" />
        </div>

        <div className="relative container-wide pt-28 sm:pt-32 pb-20 sm:pb-24 text-left">
          <div className="max-w-4xl animate-fade-up">
            <p className="eyebrow-light mb-5 sm:mb-6" data-testid="hero-eyebrow">
              ◦  India-Grown · Science-Graded · Preparing For July 2027
            </p>
            <h1 className="font-display text-[2.4rem] sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight text-balance">
              Nature's Purest Blue.
              <br className="hidden sm:block" />
              <span className="italic text-brand-gold"> Prepared for Global Launch.</span>
            </h1>
            <p className="mt-6 sm:mt-7 text-base md:text-lg text-brand-cream/80 max-w-2xl leading-relaxed">
              SPIRUVA<sup className="text-[0.5em] align-super ml-px font-medium tracking-normal">™</sup> is building a premium phycocyanin and spirulina platform for buyers planning natural-blue supply, formulation development, and regulated-market launches ahead of <span className="text-brand-gold font-mono text-sm">July 2027</span> commercial readiness.
            </p>

            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/products" data-testid="hero-cta-portfolio" className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-6 sm:px-7 py-4 text-xs uppercase tracking-[0.18em] font-medium hover:bg-brand-cream transition-all duration-300 hover:gap-3 min-h-[48px]">
                Explore Grade Portfolio <ArrowUpRight size={16} />
              </Link>
              <Link to="/reserve-allocation" data-testid="hero-cta-reserve" className="inline-flex items-center justify-center gap-2 border border-brand-cream/40 text-brand-cream px-6 sm:px-7 py-4 text-xs uppercase tracking-[0.18em] hover:bg-brand-cream hover:text-brand-black transition min-h-[48px]">
                Reserve 2027 Allocation
              </Link>
              <a
                href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/resources/company-brochure.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-brochure"
                className="inline-flex items-center justify-center gap-2 text-brand-cream/85 px-1 sm:px-2 py-4 text-xs uppercase tracking-[0.18em] hover:text-brand-gold transition min-h-[48px]"
              >
                View Company Brochure <ArrowUpRight size={14} />
              </a>
            </div>

            <p className="mt-6 sm:mt-7 text-xs text-brand-cream/60 max-w-lg leading-relaxed">
              Technical reviews, distributor discussions, and early allocation planning are now open.
            </p>

            <p className="mt-8 sm:mt-10 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-brand-cream/55 leading-relaxed">
              FSSAI · APEDA · USDA Organic · ISO 22000 · HACCP · Halal · Export-Ready Documentation Framework
            </p>
          </div>
        </div>

        <div className="hidden md:flex absolute bottom-6 left-0 right-0 items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-cream/50">
          <span className="w-8 h-px bg-brand-cream/30" /> Scroll <span className="w-8 h-px bg-brand-cream/30" />
        </div>
      </section>

      <TrustBar tone="light" />

      {/* WHY SPIRUVA */}
      <section className="container-wide py-20 md:py-32">
        <div className="max-w-3xl">
          <p className="eyebrow mb-4">◦  Why Spiruva</p>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight tracking-tight">
            Built for high-trust supply planning, <span className="italic text-brand-teal">not commodity trading.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px mt-12 md:mt-14 bg-brand-green/10">
          {PILLARS.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={title} delay={i * 90} data-testid={`pillar-${i}`} className="bg-brand-cream p-7 md:p-10 group">
              <div className="w-12 h-12 border border-brand-green/30 flex items-center justify-center text-brand-teal group-hover:bg-brand-teal group-hover:text-brand-cream group-hover:scale-110 transition-all duration-300">
                <Icon size={20} />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/40 mt-6">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-display text-2xl mt-2 text-brand-black">{title}</h3>
              <p className="text-sm text-brand-black/65 mt-3 leading-relaxed">{body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GRADE ARCHITECTURE */}
      <section className="bg-brand-cream border-y border-brand-green/15">
        <div className="container-wide py-20 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-14">
            <div className="max-w-2xl">
              <p className="eyebrow mb-4">◦  Grade Architecture</p>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight tracking-tight">
                Four grades.  <span className="italic text-brand-teal">One science-led standard.</span>
              </h2>
              <p className="text-sm md:text-base text-brand-black/65 mt-5 leading-relaxed">
                Explore the phycocyanin range by purity profile, application fit, and documentation pathway — all aligned to July 2027 launch planning.
              </p>
            </div>
            <Link to="/products" data-testid="home-link-all-products" className="inline-flex items-center gap-2 text-sm font-medium text-brand-green hover:text-brand-gold transition self-start min-h-[44px]">
              Full grade portfolio <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-brand-black text-brand-cream relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="container-wide py-16 md:py-20 relative">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-cream/10">
            {STATS.map((s, i) => (
              <Reveal key={s.l} delay={i * 110} data-testid={`stat-${i}`} className="bg-brand-black px-5 py-7 md:px-8">
                <p className="font-display text-3xl sm:text-4xl md:text-6xl text-brand-gold tracking-tight">{s.v}</p>
                <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-brand-cream/60 mt-3 leading-relaxed">{s.l}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARKET TIMING */}
      <section className="container-wide py-20 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-gold/15 text-[#8a6e1f] border border-brand-gold/40 font-mono text-[10px] uppercase tracking-[0.22em]">
              <Clock size={12} /> Market Timing · Pre-Launch Window
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight mt-6 tracking-tight">
              Natural blue demand is accelerating.
              <span className="italic text-brand-teal"> Preparation matters now.</span>
            </h2>
            <p className="text-sm md:text-base text-brand-black/70 mt-5 md:mt-6 leading-relaxed max-w-xl">
              As global brands evaluate alternatives to synthetic blue colorants and higher-purity bioactive ingredients, the advantage will belong to buyers who secure technical alignment, documentation pathways, and launch-stage allocation before commercial demand peaks.
            </p>
            <Link to="/markets" data-testid="cta-explore-markets" className="btn-primary mt-8 min-h-[48px]">
              Explore Global Markets <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="md:col-span-5">
            <div className="relative aspect-square w-full overflow-hidden border border-brand-green/20 bg-brand-black">
              <img src={POWDER_IMG} alt="Phycocyanin extract sample" className="w-full h-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-widest text-brand-cream">
                <span className="text-brand-gold">◦  Reference · Phycocyanin E25</span>
                <span>Pre-Launch Evaluation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT PARTNERS EVALUATE — proof block, not testimonials */}
      <section className="bg-brand-cream border-y border-brand-green/15">
        <div className="container-wide py-20 md:py-24">
          <div className="max-w-2xl">
            <p className="eyebrow mb-4">◦  What Partner Teams Evaluate</p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight tracking-tight">
              The questions <span className="italic text-brand-teal">serious buyers</span> ask before allocation.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 mt-10 md:mt-12">
            {[
              { q: "Can the purity ratio be held tight enough across launch-stage batches to meet our QC threshold?", a: "Quality & Compliance Teams" },
              { q: "Will documentation, traceability, and release protocols align with our regulated-market entry plan?", a: "Procurement & Regulatory Teams" },
              { q: "How is allocation being prioritised between food-grade, cosmetic-grade, and advanced technical workflows?", a: "Strategic Sourcing Leaders" },
            ].map((t, i) => (
              <Reveal key={i} delay={i * 130} className="bg-white border border-brand-green/15 p-7 md:p-8 hover:border-brand-gold hover:-translate-y-1 hover:shadow-[0_18px_40px_-15px_rgba(0,109,119,0.18)] transition-all duration-300">
                <p className="font-display text-lg md:text-xl text-brand-black/90 leading-snug">"{t.q}"</p>
                <p className="font-mono text-[11px] uppercase tracking-widest text-brand-black/55 mt-6">— {t.a}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERSHIP CTA */}
      <section className="container-wide py-20 md:py-32 text-center">
        <p className="eyebrow mb-4">◦  Partnerships</p>
        <h2 className="font-display text-3xl sm:text-4xl md:text-6xl text-brand-black leading-tight max-w-4xl mx-auto tracking-tight">
          Become a <span className="italic text-brand-teal">SPIRUVA</span> launch partner.
        </h2>
        <p className="text-sm md:text-base text-brand-black/70 mt-5 md:mt-6 max-w-2xl mx-auto leading-relaxed">
          We are reviewing distributor, importer, and strategic formulation partnerships ahead of the July 2027 commercial rollout. Qualified teams can register interest for allocation planning, territory review, and technical evaluation.
        </p>
        <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <Link to="/distributor-portal" data-testid="cta-distributor-portal" className="btn-primary min-h-[48px]">Apply to Distributor Portal <ArrowUpRight size={14} /></Link>
          <Link to="/reserve-allocation" data-testid="cta-supply-planning" className="btn-secondary min-h-[48px]">Discuss 2027 Supply Planning</Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
